import { runPullRequestCommand } from "../pullRequestCommand";
import { useId } from "react";
import { newCommandId } from "../lib/utils";
import {
  type EnvironmentId,
  type PullRequestDetail,
  type PullRequestEditInput,
  type PullRequestEditResult,
} from "@afar-research/contracts";
import { useStore } from "zustand";
import type { PullRequestEditSession } from "../pullRequestEdit";
import { PencilIcon } from "lucide-react";

import { Button } from "./ui/button";

export function PullRequestEditor({
  session,
  detail,
  environmentId,
  enabled,
  execute,
}: {
  readonly session: PullRequestEditSession;
  readonly detail: PullRequestDetail;
  readonly environmentId: EnvironmentId;
  readonly enabled: boolean;
  readonly execute: (input: PullRequestEditInput) => Promise<PullRequestEditResult>;
}) {
  const titleId = useId();
  const bodyId = useId();
  const { command, pending, result, draft } = useStore(session);
  const setDraft = (draft: PullRequestEditInput | null) => session.setState({ draft });
  const unresolved = command !== null && result?.status !== "settled";
  const run = (input: PullRequestEditInput) =>
    runPullRequestCommand({
      session,
      input,
      enabled,
      execute,
      onStart: () => setDraft(null),
    });
  return (
    <div aria-label="Edit Pull Request">
      {result ? (
        <p role="status" className="mb-3 text-sm">
          {result.status === "settled"
            ? result.receipt.status === "accepted"
              ? "Changes saved."
              : result.receipt.result.message
            : result.message}
        </p>
      ) : null}
      {pending ? (
        <p role="status">Saving changes…</p>
      ) : unresolved ? (
        <Button disabled={!enabled} onClick={() => void run(command)} size="sm">
          Check outcome
        </Button>
      ) : draft ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (draft.title.trim()) void run(draft);
          }}
          className="grid gap-3"
        >
          <div className="grid gap-1 text-sm">
            <label htmlFor={titleId}>Title</label>
            <input
              id={titleId}
              className="rounded border p-2"
              value={draft.title}
              maxLength={256}
              required
              disabled={!enabled}
              onChange={(event) => setDraft({ ...draft, title: event.target.value })}
            />
          </div>
          <div className="grid gap-1 text-sm">
            <label htmlFor={bodyId}>Description</label>
            <textarea
              id={bodyId}
              className="min-h-40 rounded border p-2"
              value={draft.body}
              maxLength={65536}
              disabled={!enabled}
              onChange={(event) => setDraft({ ...draft, body: event.target.value })}
            />
          </div>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={!enabled || !draft.title.trim()}>
              Save changes
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setDraft(null)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <Button
          aria-label="Edit title and description"
          size="icon-xs"
          variant="ghost"
          className="shrink-0 text-muted-foreground"
          disabled={!enabled || detail.projectRevision === undefined}
          onClick={() => {
            if (!detail.projectRevision) return;
            setDraft({
              environmentId,
              projectId: detail.projectId,
              repository: detail.repository,
              number: detail.number,
              commandId: newCommandId(),
              expectedRevision: detail.projectRevision,
              resourceVersion: detail.resourceVersion,
              title: detail.title,
              body: detail.body,
            });
          }}
        >
          <PencilIcon aria-hidden className="size-3" />
          {/* The name stays in the accessible name and in the text, where the pencil alone
              would say nothing to a reader who cannot see it. */}
          <span className="sr-only">Edit title and description</span>
        </Button>
      )}
    </div>
  );
}
